import React, { type ButtonHTMLAttributes, type ReactNode, type TouchEvent } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const baseClasses =
  'inline-flex items-center justify-center rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed';

const variantClasses: Record<Variant, string> = {
  primary: 'bg-[#007c86] text-white border border-[#007c86] hover:bg-[#006670]',
  secondary: 'bg-gray-50 text-gray-900 border border-gray-300 hover:bg-gray-100',
  ghost: 'bg-transparent text-gray-800 hover:bg-gray-50',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth,
  leftIcon,
  rightIcon,
  className = '',
  children,
  onClick,
  onTouchStart,
  disabled,
  ...props
}: ButtonProps) {
  // BUG FIX #19: iOS touch handling - trigger onClick on touchStart to prevent double-tap
  // On iOS, the first tap often dismisses keyboard, second tap triggers button
  // By handling touchStart, we ensure button triggers immediately on first tap
  const touchStartRef = React.useRef<boolean>(false);
  
  const handleTouchStart = (e: TouchEvent<HTMLButtonElement>) => {
    // Only handle if button is not disabled and onClick exists
    if (disabled || !onClick) {
      if (onTouchStart) {
        onTouchStart(e);
      }
      return;
    }

    // Prevent double-firing by checking if we've already handled this touch
    if (touchStartRef.current) {
      return;
    }
    
    touchStartRef.current = true;
    
    // Prevent default to avoid double-firing with onClick
    e.preventDefault();
    e.stopPropagation();
    
    // Trigger onClick immediately on touchStart (before keyboard dismissal)
    // Create a synthetic mouse event for onClick handler
    const syntheticEvent = {
      ...e,
      preventDefault: () => {},
      stopPropagation: () => {},
      currentTarget: e.currentTarget,
      target: e.target,
      nativeEvent: e.nativeEvent,
    } as unknown as React.MouseEvent<HTMLButtonElement>;
    
    onClick(syntheticEvent);
    
    // Reset flag after a short delay to allow subsequent touches
    setTimeout(() => {
      touchStartRef.current = false;
    }, 300);

    // Call original onTouchStart if provided
    if (onTouchStart) {
      onTouchStart(e);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // BUG FIX #19: Prevent onClick from firing if touchStart already handled it
    // This prevents double-firing on devices that support both touch and mouse
    if (touchStartRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      className={[
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        fullWidth ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      disabled={disabled}
      style={{ touchAction: 'manipulation', ...props.style }}
      {...props}
    >
      {leftIcon && <span className="mr-2 text-lg">{leftIcon}</span>}
      <span>{children}</span>
      {rightIcon && <span className="ml-2 text-lg">{rightIcon}</span>}
    </button>
  );
}


